import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme context — light / dark / system.
 *
 * The provider supports three user-facing modes:
 * - `light`: forces light theme regardless of OS preference.
 * - `dark`: forces dark theme regardless of OS preference.
 * - `system`: follows the OS preference (`prefers-color-scheme`), updating
 *   live as the OS preference changes.
 *
 * The provider exposes both the resolved `theme` (`'light' | 'dark'`) and
 * the user `mode` (`'light' | 'dark' | 'system'`). Components that switch
 * iconography or copy based on what is actually shown use `theme`; UI that
 * lets the user toggle the preference reads/writes `mode`.
 *
 * Persistence: when the user picks `light` or `dark`, the choice is written
 * to `localStorage[STORAGE_KEY]`. When the user picks `system`, the key is
 * removed. The provider rehydrates from `localStorage` on mount.
 */

export type Theme = 'light' | 'dark';
export type ThemeMode = Theme | 'system';

const STORAGE_KEY = 'sica-theme';

interface ThemeContextValue {
  /** What is currently rendered. */
  theme: Theme;
  /** What the user chose. */
  mode: ThemeMode;
  /** Set the user preference. Persists `light`/`dark` to localStorage; `system` clears it. */
  setMode: (mode: ThemeMode) => void;
  /** Convenience: flip between light and dark, ignoring `system`. */
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'system';
}

function readSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({
  children,
  defaultMode,
}: {
  children: ReactNode;
  /**
   * Initial mode if `localStorage` has no prior choice. Defaults to `'system'`,
   * which means "follow the OS". Pass `'light'` to make a surface light-first
   * (e.g. the public site at v0).
   */
  defaultMode?: ThemeMode;
}) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = readStoredMode();
    if (stored !== 'system') return stored;
    return defaultMode ?? 'system';
  });

  const [systemTheme, setSystemTheme] = useState<Theme>(() => readSystemTheme());

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const theme: Theme = mode === 'system' ? systemTheme : mode;

  useEffect(() => {
    const root = document.documentElement;
    if (mode === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', mode);
    }
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window === 'undefined') return;
    if (next === 'system') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(theme === 'dark' ? 'light' : 'dark');
  }, [setMode, theme]);

  const value = useMemo(
    () => ({ theme, mode, setMode, toggle }),
    [theme, mode, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside a <ThemeProvider>');
  }
  return ctx;
}
